import React, { useState } from 'react';
import { MODULE_STATUS } from 'constants/modules';
import {
  AuthorizationErrorResponse,
  SocialLoginBusiness,
  SocialLoginDoneResponse,
} from 'types/SocialLogin';
import { ModuleStatus } from 'types/ModuleStatus';
import { NewAddedPage } from 'types/Page';
import Modal from 'components/shared/Modal';
import { ActivityIndicator, Alert, Button } from '@kontentino/ui';
import { useTranslation } from 'react-i18next';
import GenericProfilesSelectForm from 'components/modals/newSocialMediaProfile/automaticForm/GenericProfilesSelectForm';
import { useQuery } from 'react-query';
import { queryKey } from 'constants/queryKey';
import SocialLoginApi from 'app/modules/socialLogin/api';
import { useToast } from 'app/hooks/useToast';
import ErrorStepsGuide from '../ErrorStepsGuide';
import { ApiClientError } from 'api/client';
import HeapUtils from 'app/utils/heap';
import ManualSystemUserTokenForm from 'components/modals/newSocialMediaProfile/manualToken/ManualSystemUserTokenForm';
import { useSocialLoginContext } from 'app/modules/socialLogin/context';
import SelectBusinessPortfolio from 'components/modals/newSocialMediaProfile/selectSocialProfiles/SelectBusinessPortfolio';
import {
  oboOutcomeHelpUrl,
  oboOutcomeMessageKey,
} from 'app/modules/socialLogin/utils/oboOutcome';
import { openBlank } from 'utils/url';

interface Props {
  status: ModuleStatus;
  pageType: number;
  pageId?: number;
  authorization?: SocialLoginDoneResponse;
  error?: AuthorizationErrorResponse;
  onPageAdded(addedPage: NewAddedPage): void;
  onGoBack(): void;
  isSingleSelect?: boolean;
  isLoading?: boolean;
}

const Form = (props: Props) => {
  const { t } = useTranslation();
  const [showManualTokenForm, setShowManualTokenForm] = useState(false);
  const [selectedBusiness, setSelectedBusiness] =
    useState<SocialLoginBusiness | null>(null);
  const connectedPagesExternalIds = useQuery(
    queryKey.socialLoginConnectedPagesExternalIds(),
    () => SocialLoginApi.connectedPagesExternalIds(props.pageType),
    {
      onError: (error: ApiClientError) => {
        HeapUtils.track(
          HeapUtils.events.SOCIAL_LOGIN_FETCH_CONNECTED_EXTERNAL_PAGES_ERROR,
          {
            page_type: props.pageType,
            error_reason: error?.message
              ? error.message
              : error?.userMessage ?? 'Unknown error',
          },
        );
      },
    },
  );
  const toast = useToast();
  const { businesses, userKey, clientBusinessId } = useSocialLoginContext();

  // Key the OBO UI off the poll-provided `businesses` (returned only for the OBO
  // flow and persisted across the OAuth full-page redirect via the userKey in the
  // URL) — NOT off `activeTokenType`, which is ephemeral useState wiped by the
  // redirect back from Facebook.
  const isObo = !!businesses?.length;

  const handleOboError = (error: ApiClientError) => {
    const outcome = error.response?.data?.outcome as string | undefined;
    const messageKey = oboOutcomeMessageKey(outcome);
    const helpUrl = oboOutcomeHelpUrl(outcome);

    if (helpUrl) {
      toast(t(messageKey), 'error', {
        cta: {
          label: t('learnMore'),
          onClick: () => openBlank(helpUrl),
        },
      });
    } else {
      toast(t(messageKey), 'error');
    }
  };

  const handleOboSuccess = (addedPage: NewAddedPage) => {
    props.onPageAdded(addedPage);
    setSelectedBusiness(null);
  };

  return (
    <div>
      <Modal.Content>
        <div className="tw-flex tw-min-w-[400px] tw-flex-col tw-items-center tw-justify-center">
          {(props.isLoading || connectedPagesExternalIds.isLoading) && (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-6">
              <ActivityIndicator className="tw-h-12 tw-w-12" />
              <p className="tw-mt-6 tw-text-md">
                {t('newSocialMediaProfile.pleaseWait')}
              </p>
            </div>
          )}

          {props.status === MODULE_STATUS.Failed && (
            <>
              <Alert
                variant="danger"
                cta={{
                  label: t('goBack'),
                  onClick: props.onGoBack,
                }}
                title={t('unableToAuthorizeSocialLogin')}
                className="tw-my-5"
              >
                {typeof props.error?.reason === 'string' && (
                  <div>{props.error.reason}</div>
                )}
              </Alert>
              <div className="tw-mt-3 tw-pb-6">
                <ErrorStepsGuide pageType={props.pageType} />
              </div>
              <div className="tw-flex tw-w-full tw-flex-col tw-gap-4 tw-pb-6">
                {!showManualTokenForm ? (
                  <Button
                    variant="ghost"
                    onClick={() => setShowManualTokenForm(true)}
                    data-cy="safe-connect_manual-token-fallback"
                  >
                    {t('newSocialMediaProfile.systemUserToken.fallbackCta')}
                  </Button>
                ) : (
                  <ManualSystemUserTokenForm pageType={props.pageType} />
                )}
              </div>
            </>
          )}
        </div>

        {isObo && !selectedBusiness && !connectedPagesExternalIds.isLoading && (
          <SelectBusinessPortfolio
            businesses={businesses ?? []}
            pageType={props.pageType}
            onSelect={setSelectedBusiness}
          />
        )}

        {isObo && selectedBusiness && !connectedPagesExternalIds.isLoading && (
          <>
            <Button
              variant="ghost"
              className="tw-mb-3 tw-self-start"
              onClick={() => setSelectedBusiness(null)}
              data-cy="business-portfolio_connect-another"
              data-testid="business-portfolio_connect-another"
            >
              {t('newSocialMediaProfile.oboTokenType.connectAnother')}
            </Button>
            <GenericProfilesSelectForm
              connectedPagesExternalIds={connectedPagesExternalIds.data || []}
              page={{
                type: props.pageType,
                id: props.pageId,
              }}
              data={selectedBusiness.pages}
              isPartial={selectedBusiness.partial}
              clientBusinessId={selectedBusiness.id}
              tokenType="system_user_obo"
              userKey={userKey}
              isSingleSelect={props.isSingleSelect}
              onPagesCreate={handleOboSuccess}
              onError={handleOboError}
            />
          </>
        )}

        {!isObo &&
          props.authorization &&
          !connectedPagesExternalIds.isLoading && (
            <GenericProfilesSelectForm
              connectedPagesExternalIds={connectedPagesExternalIds.data || []}
              page={{
                type: props.pageType,
                id: props.pageId,
              }}
              data={props.authorization.data}
              isPartial={props.authorization.partial}
              clientBusinessId={clientBusinessId}
              isSingleSelect={props.isSingleSelect}
              onPagesCreate={props.onPageAdded}
              onError={(e) => {
                toast(e?.userMessage ?? t('somethingWentWrong'), 'error');
              }}
            />
          )}
      </Modal.Content>
    </div>
  );
};

export default Form;
